import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  AddBusiness as AddBusinessIcon,
  AutoAwesome as AutoAwesomeIcon,
  Email as EmailIcon,
  Handshake as HandshakeIcon,
  Login as LoginIcon,
  PriceCheck as PriceCheckIcon,
  ReceiptLong as ReceiptLongIcon,
  RocketLaunch as RocketLaunchIcon,
  SmartToy as SmartToyIcon,
  Storefront as StorefrontIcon,
  SupportAgent as SupportAgentIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PartnerLanguageSelector from '../../components/partner/PartnerLanguageSelector';
import { partnerTheme, partnerHeroGradient } from '../../components/partner/partnerTheme';
import { employeePortalUrl } from '../../utils/employeePortal';

// Página COMERCIAL del programa de partners: pública (sin auth, fuera de
// PartnerProtectedRoute y sin PartnerLayout, que asume sesión). Es la landing a
// la que apunta el login del portal partner para quien todavía no lo es.
//
// Regla de contenido: NADA de cifras. El descuento y los precios se cierran uno
// a uno en la conversación comercial, así que aquí se habla siempre de «tu
// descuento acordado»; prometer un número en una página pública sería un
// compromiso que el marketplace no ha dado.

const CONTACT_EMAIL = 'wai@waiworking.com';

const PartnerProgramPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const mailto =
    `mailto:${CONTACT_EMAIL}` +
    `?subject=${encodeURIComponent(
      t('partner.program.mailSubject', 'Quiero ser partner de WAI')
    )}` +
    `&body=${encodeURIComponent(
      t(
        'partner.program.mailBody',
        'Hola:\n\nMe interesa el programa de partners de WAI.\n\nEmpresa:\nPersona de contacto:\nTeléfono:\nTipo de clientes con los que trabajo:\n\nGracias.'
      )
    )}`;

  // Propuesta de valor — las tres razones por las que un consultor/integrador
  // revende WAI en vez de recomendarlo y quedarse fuera de la relación.
  const valueProps = [
    {
      icon: <PriceCheckIcon fontSize="large" />,
      title: t('partner.program.value1Title', 'Compras con tu descuento y pones tú el precio'),
      text: t(
        'partner.program.value1Text',
        'Compras créditos al por mayor con el descuento que acordemos contigo: ese descuento es tu margen. El precio al que se los repercutes a tus clientes lo decides tú, cliente a cliente, sin que nadie se meta en tu relación comercial.'
      )
    },
    {
      icon: <TuneIcon fontSize="large" />,
      title: t('partner.program.value2Title', 'Tu portal: sus altas, sus empleados IA y sus créditos'),
      text: t(
        'partner.program.value2Text',
        'Das de alta a tus clientes, les asignas empleados IA del catálogo y les repartes créditos desde tu bolsa mayorista, todo desde tu propio portal. Puedes entrar a ver la cuenta de un cliente en modo solo lectura para ayudarle sin pedirle credenciales.'
      )
    },
    {
      icon: <ReceiptLongIcon fontSize="large" />,
      title: t('partner.program.value3Title', 'Facturación mensual, sin pasarela de pago'),
      text: t(
        'partner.program.value3Text',
        'Nada de tarjetas ni suscripciones por cliente: cada mes recibes una única factura con tus compras del mes y tu descuento ya aplicado, con el desglose de lo que has asignado a cada cliente. Tú facturas a los tuyos como quieras.'
      )
    }
  ];

  // Qué vende realmente el partner: empleados IA que trabajan, no licencias.
  const highlights = [
    {
      icon: <SmartToyIcon />,
      text: t(
        'partner.program.highlight1',
        'Empleados IA ya construidos y mantenidos: tu cliente contrata uno y lo tiene trabajando, sin proyecto de desarrollo.'
      )
    },
    {
      icon: <AutoAwesomeIcon />,
      text: t(
        'partner.program.highlight2',
        'Se conectan a las herramientas que tu cliente ya usa y trabajan con la identidad del usuario que los administra.'
      )
    },
    {
      icon: <SupportAgentIcon />,
      text: t(
        'partner.program.highlight3',
        'Tú te quedas con la relación y el conocimiento del negocio; nosotros ponemos la plataforma, el despliegue y el mantenimiento.'
      )
    }
  ];

  return (
    <ThemeProvider theme={partnerTheme}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Hero */}
        <Box sx={{ background: partnerHeroGradient, color: '#fff', pb: { xs: 8, md: 12 } }}>
          <Container maxWidth="lg">
            {/* Cabecera mínima: marca + idioma + acceso al portal para quien
                YA es partner. El selector va AQUÍ porque esta página es pública
                y no lleva navbar: sin él, un visitante en inglés no tendría
                forma de cambiar el idioma de la landing. */}
            <Box display="flex" alignItems="center" py={3} gap={1}>
              <HandshakeIcon sx={{ fontSize: 30 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, flexGrow: 1 }}>
                WAI Partner
              </Typography>
              <PartnerLanguageSelector onHero />
              <Button
                color="inherit"
                startIcon={<LoginIcon />}
                onClick={() => navigate('/partner/login')}
                sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
              >
                {t('partner.program.loginCta', 'Ya soy partner')}
              </Button>
            </Box>

            <Box maxWidth={820} pt={{ xs: 2, md: 5 }}>
              <Chip
                label={t('partner.program.badge', 'Programa de partners')}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', mb: 2, fontWeight: 700 }}
              />
              <Typography
                variant="h3"
                sx={{ mb: 2, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.1 }}
              >
                {t('partner.program.heroTitle', 'Vende empleados IA a tus clientes con tu marca y tu margen')}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, mb: 4 }}>
                {t(
                  'partner.program.heroSubtitle',
                  'Conviértete en partner de WAI: compras créditos con tu descuento acordado, decides el precio a tus clientes y gestionas sus cuentas, sus empleados IA y sus créditos desde tu propio portal.'
                )}
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<EmailIcon />}
                  href={mailto}
                  sx={{ bgcolor: '#fff', color: 'primary.dark', '&:hover': { bgcolor: '#F1F5FF' } }}
                >
                  {t('partner.program.heroCta', 'Quiero ser partner')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<StorefrontIcon />}
                  href={employeePortalUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: '#fff' } }}
                >
                  {t('partner.program.catalogCta', 'Ver el catálogo de empleados')}
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ mt: { xs: -5, md: -8 }, pb: 8 }}>
          {/* Propuesta de valor */}
          <Grid container spacing={3} mb={6}>
            {valueProps.map((prop) => (
              <Grid item xs={12} md={4} key={prop.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Box color="primary.main" mb={1.5}>{prop.icon}</Box>
                    <Typography variant="h6" gutterBottom>{prop.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{prop.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Qué vendes exactamente */}
          <Card sx={{ mb: 6 }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <SmartToyIcon color="primary" fontSize="large" />
                <Typography variant="h5">
                  {t('partner.program.whatTitle', 'Qué vendes: empleados IA, no licencias')}
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" mb={3}>
                {t(
                  'partner.program.whatText',
                  'El catálogo de WAI son empleados IA especializados por oficio. Tu cliente contrata el que necesita, le pone nombre y designa al usuario que lo administra; a partir de ahí trabaja dentro de su negocio, y su consumo se descuenta de los créditos que tú le has asignado.'
                )}
              </Typography>
              <Grid container spacing={2}>
                {highlights.map((h, i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <Box display="flex" gap={1.5}>
                      <Box color="primary.main" sx={{ mt: '2px' }}>{h.icon}</Box>
                      <Typography variant="body2" color="text.secondary">{h.text}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Cómo funciona, en 3 pasos */}
          <Card sx={{ mb: 6 }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                <RocketLaunchIcon color="primary" fontSize="large" />
                <Typography variant="h5">
                  {t('partner.program.howTitle', 'Cómo funciona, en 3 pasos')}
                </Typography>
              </Box>
              {/* Stepper no interactivo: es una explicación, no un flujo — todos
                  los pasos van abiertos para que se lean de un vistazo. */}
              <Stepper orientation="vertical" activeStep={-1}>
                <Step active expanded>
                  <StepLabel icon={<HandshakeIcon color="primary" />}>
                    <Typography fontWeight={700}>
                      {t('partner.program.step1Title', 'Acordamos tus condiciones y abrimos tu portal')}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {t(
                        'partner.program.step1Text',
                        'Hablamos de tus clientes y cerramos tu descuento mayorista, cuántas cuentas vas a gestionar y cuántos empleados IA puedes tener desplegados. Te damos de alta y recibes el acceso a tu portal de partner.'
                      )}
                    </Typography>
                  </StepContent>
                </Step>
                <Step active expanded>
                  <StepLabel icon={<AddBusinessIcon color="primary" />}>
                    <Typography fontWeight={700}>
                      {t('partner.program.step2Title', 'Das de alta a tus clientes y les asignas empleados')}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {t(
                        'partner.program.step2Text',
                        'Creas la cuenta de cada cliente desde tu portal, eliges del catálogo el empleado IA que le encaja y le repartes créditos de tu bolsa mayorista. El despliegue lo hacemos nosotros: tu cliente entra y ya lo tiene funcionando.'
                      )}
                    </Typography>
                  </StepContent>
                </Step>
                <Step active expanded>
                  <StepLabel icon={<ReceiptLongIcon color="primary" />}>
                    <Typography fontWeight={700}>
                      {t('partner.program.step3Title', 'Cobras a tus clientes y recibes una factura al mes')}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        'partner.program.step3Text',
                        'Tú facturas a tus clientes al precio que hayas puesto. A fin de mes te llega una única factura con tus compras y tu descuento aplicado, con el desglose por cliente para que cuadres tus números sin trabajo extra.'
                      )}
                    </Typography>
                  </StepContent>
                </Step>
              </Stepper>
            </CardContent>
          </Card>

          {/* Contacto — la llamada a la acción principal */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: partnerHeroGradient,
              color: '#fff'
            }}
          >
            <EmailIcon sx={{ fontSize: 44, mb: 1 }} />
            <Typography variant="h4" sx={{ mb: 1.5 }}>
              {t('partner.program.contactTitle', '¿Hablamos?')}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, maxWidth: 680, mx: 'auto' }}>
              {t(
                'partner.program.contactText',
                'Cuéntanos con qué clientes trabajas y te preparamos una propuesta con tu descuento y tus cupos. Sin compromiso y sin alta automática: cada partner se abre a mano.'
              )}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<EmailIcon />}
              href={mailto}
              sx={{ bgcolor: '#fff', color: 'primary.dark', '&:hover': { bgcolor: '#F1F5FF' } }}
            >
              {t('partner.program.contactCta', 'Escríbenos a {{email}}', { email: CONTACT_EMAIL })}
            </Button>
            <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.25)' }} />
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {t('partner.program.alreadyPartner', '¿Ya eres partner de WAI?')}{' '}
              <Box
                component="span"
                onClick={() => navigate('/partner/login')}
                sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }}
              >
                {t('partner.program.alreadyPartnerCta', 'Entra en tu portal')}
              </Box>
            </Typography>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default PartnerProgramPage;
